import {
  Item,
  setCarouselInteraction,
  updateUserWithNewCartTotal,
} from "~/userAnalytics";
import { sendGtmEvent, clearEcommerceInDataLayer } from "../definitions";
import {
  sendFacebookPixelAddToCartEvents,
  sendPinterestAddToCartEvents,
} from "./addToCartApiHelpers";
import { EcommerceData, formatEcommerceData } from "./addToCartHelpers";
import { sendCustomerIoUpdateCartEvent } from "../CustomerIo/CustomerIoEvents";
import {
  createProducts,
  formatProductsForCustomerIo,
  formatDefaultProducts,
} from "../productHelpers";

export type AtcAnalyticsProps = {
  source: string;
  carouselProps?: {
    carouselName: string;
    carouselModel: string;
    position: number;
    carouselPlacementId?: string;
    shouldSetInteraction?: boolean;
  };
  appliedCoupons?: NodeListOf<HTMLInputElement>;
  skipCustomerIo?: boolean;
};

export const addToCartAnalytics = async (
  atcProps: AtcAnalyticsProps,
  ...items: Item[]
) => {
  let total = 0;
  const feedIdentifiers: string[] = [];

  if (atcProps?.carouselProps?.shouldSetInteraction) {
    items.forEach((item) => {
      setCarouselInteraction(
        item.itemNumber,
        atcProps?.carouselProps?.carouselPlacementId ?? ""
      );
    });
  }

  const baseProductsData = await createProducts(items, true);

  const products = formatDefaultProducts(baseProductsData);
  const customerIoProducts = formatProductsForCustomerIo(
    baseProductsData,
    true,
    true,
    true
  );

  if (products.length === 0) {
    return;
  }
  for (const product of products) {
    total += Number(product?.price ?? 0) * Number(product?.quantity ?? 0);
    if (product.feedIdentifier) {
      feedIdentifiers.push(product.feedIdentifier);
    }
  }
  // Done to avoid any floating point errors
  total = parseFloat(total.toFixed(2));
  // Note that prices here are only using P1, as the actual price can vary depending on user to user, so may vary slightly from the actual value.
  const newCartTotal = await updateUserWithNewCartTotal(true, total);
  if (newCartTotal.isError) {
    return;
  }
  sendPinterestAddToCartEvents(products);
  sendFacebookPixelAddToCartEvents(feedIdentifiers, total.toString());
  const formattedEcommerceProducts = formatEcommerceData(products, {
    total,
    position: atcProps.carouselProps?.position ?? 0,
  });

  let coupons = "";
  if (atcProps.appliedCoupons) {
    coupons = Array.from(atcProps.appliedCoupons)
      .map((element) => element?.getAttribute("data-discount-code"))
      .join(",");
  }

  sendGtmAddToCartEvents(
    formattedEcommerceProducts,
    newCartTotal.value,
    atcProps
  );
  if (!atcProps.skipCustomerIo) {
    customerIoProducts.forEach((customerIoProduct) => {
      sendCustomerIoUpdateCartEvent(
        "Add to Cart",
        [customerIoProduct],
        coupons
      );
    });
  }
};

export const removeFromCartAnalytics = async (
  items: Item[],
  couponsApplied: NodeListOf<HTMLInputElement>,
  emptyCart = false
) => {
  const itemTotalCosts = items.map(
    (item) => (item?.price ?? 0) * (item?.quantity ?? 0)
  );
  const totalCost = itemTotalCosts.reduce((acc, curr) => acc + curr, 0);
  const newCartTotal = await updateUserWithNewCartTotal(false, totalCost);
  if (newCartTotal.isError) {
    return;
  }
  const baseProductsData = await createProducts(items, true);
  if (baseProductsData.length === 0) {
    return;
  }
  const products = formatDefaultProducts(baseProductsData);
  const customerIoProducts = formatProductsForCustomerIo(
    baseProductsData,
    true,
    true,
    true
  );

  const formattedEcommerceProduct = formatEcommerceData(products, {
    total: totalCost,
  });

  let coupons = "";
  if (couponsApplied) {
    coupons = Array.from(couponsApplied)
      .map((element) => element.getAttribute("data-discount-code"))
      .join(",");
  }

  clearEcommerceInDataLayer();
  sendGtmEvent({
    event: "productRemoveFromCart",
    cart_total: newCartTotal.value,
    ecommerce: formattedEcommerceProduct,
  });
  if (emptyCart) {
    sendCustomerIoUpdateCartEvent("Empty Cart", [], "");
  } else {
    customerIoProducts.forEach((customerIoProduct) => {
      sendCustomerIoUpdateCartEvent(
        "Remove from Cart",
        [customerIoProduct],
        coupons
      );
    });
  }
};

const sendGtmAddToCartEvents = (
  ecommerce: EcommerceData,
  cartTotal: number,
  atcProps: AtcAnalyticsProps
) => {
  clearEcommerceInDataLayer();
  sendGtmEvent({
    event: "addToCart",
    atc_source: atcProps.carouselProps?.carouselName
      ? "carousel"
      : atcProps.source,
    cart_total: cartTotal,
    ecommerce: { ...ecommerce },
    // Reset carousel props to empty string if not present otherwise variables will be previous values
    carouselName: atcProps.carouselProps?.carouselName ?? "",
    carouselModel: atcProps.carouselProps?.carouselModel ?? "",
  });
};
